import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ModelPage } from "../types/cuadra";

export interface UseModelSelectionResult {
  currentModelId: string | null;
  handleModelChange: (newModelId: string) => void;
  handleModelSelectorSync: (syncModelId: string) => void;
  resolvedSupportsVision: boolean;
  resolvedEnableReasoning: boolean;
  hasModel: boolean;
}

/**
 * Manages model selection state, auto-selects the first model when
 * models load, and derives vision/reasoning support from the selected model.
 */
export function useModelSelection(opts: {
  modelMode: "selector" | "fixed";
  modelId?: string;
  modelsData: ModelPage | null;
  supportsVision?: boolean;
  enableReasoning?: boolean;
  onModelChange?: (modelId: string) => void;
}): UseModelSelectionResult {
  const {
    modelMode,
    modelId,
    modelsData,
    supportsVision,
    enableReasoning,
    onModelChange,
  } = opts;

  const [currentModelId, setCurrentModelId] = useState<string | null>(
    modelMode === "fixed" ? modelId || null : null,
  );
  const prevModelIdRef = useRef<string | null>(currentModelId);

  const handleModelChange = useCallback(
    (newModelId: string) => {
      setCurrentModelId(newModelId);
      prevModelIdRef.current = newModelId;
      onModelChange?.(newModelId);
    },
    [onModelChange],
  );

  const handleModelSelectorSync = useCallback((syncModelId: string) => {
    setCurrentModelId(syncModelId);
    prevModelIdRef.current = syncModelId;
  }, []);

  useEffect(() => {
    if (
      modelMode === "selector" &&
      modelsData?.items &&
      modelsData.items.length > 0 &&
      !currentModelId
    ) {
      const firstModel = modelsData.items[0];
      if (firstModel?.id) {
        const id = firstModel.id as string;
        setCurrentModelId(id);
        prevModelIdRef.current = id;
      }
    }
  }, [modelMode, modelsData?.items, currentModelId]);

  const hasModel = modelMode === "fixed" ? !!modelId : !!currentModelId;

  const resolvedSupportsVision = useMemo(() => {
    if (supportsVision !== undefined) return supportsVision;
    if (modelMode === "selector" && currentModelId && modelsData?.items) {
      const selectedModel = modelsData.items.find(
        (m) => m.id === currentModelId,
      );
      return selectedModel?.supportsVision ?? true;
    }
    return true;
  }, [supportsVision, modelMode, currentModelId, modelsData?.items]);

  const resolvedEnableReasoning = useMemo(() => {
    if (enableReasoning !== undefined) return enableReasoning;
    if (modelMode === "selector" && currentModelId && modelsData?.items) {
      const selectedModel = modelsData.items.find(
        (m) => m.id === currentModelId,
      );
      return selectedModel?.supportsReasoning ?? false;
    }
    return false;
  }, [enableReasoning, modelMode, currentModelId, modelsData?.items]);

  return {
    currentModelId,
    handleModelChange,
    handleModelSelectorSync,
    resolvedSupportsVision,
    resolvedEnableReasoning,
    hasModel,
  };
}
